import { NextApiHandler } from 'next'

import { isSignupModalVariant } from '../experiments/signup-modal/flag'
import { isSignupModalSegment } from '../experiments/signup-modal/segment'
import {
  isSignupModalCtaType,
  isSignupModalRenderOutcome,
  recordSignupModalCtaClickedHealth,
  recordSignupModalFallbackUsedHealth,
  recordSignupModalRenderedHealth,
} from '../metrics/signup-modal-metrics'
import { isAllowedApiOrigin } from './allowed-origin'

export interface SignupModalMetricsBridgeResponse {
  recorded: boolean
}

type RejectReason = 'origin' | 'parse' | 'label' | 'event' | 'recorder'

// Tags each fail-open recorded:false so empty health panels stay debuggable —
// a bad origin reads apart from a recorder outage. No PII (reason + origin host).
function logRejected(reason: RejectReason, origin?: string): void {
  console.warn('[signup-modal-metrics-bridge] rejected beacon', { reason, origin })
}

export const signupModalMetricsBridgeHandler: NextApiHandler<
  SignupModalMetricsBridgeResponse
> = async (req, res) => {
  if (req.method !== 'POST') {
    res.setHeader('Allow', 'POST')
    res.status(405).end()
    return
  }

  res.setHeader('Cache-Control', 'no-store')

  // 200 not 403: the fire-and-forget caller can't act on a 4xx.
  if (!isAllowedApiOrigin(req.headers.origin)) {
    logRejected('origin', req.headers.origin)
    res.status(200).json({ recorded: false })
    return
  }

  const body = req.body
  if (body == null || typeof body !== 'object') {
    logRejected('parse', req.headers.origin)
    res.status(200).json({ recorded: false })
    return
  }

  const { event, variant, segment, appId, outcome, ctaType, inExperiment } = body as Record<
    string,
    unknown
  >
  if (
    !isSignupModalVariant(variant) ||
    !isSignupModalSegment(segment) ||
    typeof appId !== 'string'
  ) {
    logRejected('label', req.headers.origin)
    res.status(200).json({ recorded: false })
    return
  }

  const assigned = inExperiment === true

  try {
    if (event === 'render' && isSignupModalRenderOutcome(outcome)) {
      if (outcome === 'fallback_used') {
        await recordSignupModalFallbackUsedHealth({
          variant,
          segment,
          appId,
          inExperiment: assigned,
        })
      } else {
        await recordSignupModalRenderedHealth({ variant, segment, appId, inExperiment: assigned })
      }
    } else if (event === 'cta' && isSignupModalCtaType(ctaType)) {
      await recordSignupModalCtaClickedHealth({
        variant,
        segment,
        appId,
        ctaType,
        inExperiment: assigned,
      })
    } else {
      logRejected('event', req.headers.origin)
      res.status(200).json({ recorded: false })
      return
    }
  } catch {
    // Fail-open: a Prometheus record failure must never break the auth flow.
    logRejected('recorder', req.headers.origin)
    res.status(200).json({ recorded: false })
    return
  }

  res.status(200).json({ recorded: true })
}

export const signupModalMetricsBridgeApiConfig = {
  api: {
    bodyParser: { sizeLimit: '4kb' },
  },
}
